<template>
  <v-dialog
    :model-value="unref(modelValue)"
    max-width="440"
    persistent
    content-class="confirm-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="confirm-dialog__card">
      <v-card-title class="confirm-dialog__title text-h6">{{ displayTitle }}</v-card-title>
      <v-card-text class="confirm-dialog__text">
        <p class="text-body-2 mb-0">{{ unref(message) }}</p>
      </v-card-text>
      <v-card-actions class="confirm-dialog__actions">
        <v-spacer />
        <v-btn
          variant="outlined"
          class="confirm-dialog__btn confirm-dialog__btn--cancel"
          @click="$emit('update:modelValue', false)"
        >
          {{ t('actions.cancel') }}
        </v-btn>
        <v-btn
          :color="confirmColor"
          variant="flat"
          class="confirm-dialog__btn confirm-dialog__btn--confirm"
          :loading="unref(loading)"
          :disabled="unref(loading)"
          :prepend-icon="confirmIcon"
          @click="$emit('confirm')"
        >
          {{ displayConfirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { unref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: [Boolean, Object],
    default: false
  },
  title: {
    type: [String, Object],
    default: ''
  },
  message: {
    type: [String, Object],
    required: true
  },
  confirmLabel: {
    type: String,
    default: ''
  },
  confirmColor: {
    type: String,
    default: 'error'
  },
  confirmIcon: {
    type: String,
    default: 'mdi-delete'
  },
  loading: {
    type: [Boolean, Object],
    default: false
  }
})

defineEmits(['update:modelValue', 'confirm'])

const { t } = useI18n()

const displayTitle = computed(() => {
  const v = unref(props.title)
  return v != null && String(v).trim() !== '' ? String(v) : t('confirm.default_title')
})

const displayConfirmLabel = computed(() => {
  const v = props.confirmLabel
  return v != null && String(v).trim() !== '' ? String(v) : t('actions.delete')
})
</script>

<style scoped>
/* Diálogo más oscuro y redondeado, alineado con el resto de la app */
.confirm-dialog :deep(.v-overlay__content) {
  border-radius: 24px !important;
  overflow: hidden;
}

.confirm-dialog__card {
  background: rgba(30, 30, 33, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px !important;
  overflow: hidden;
}

.confirm-dialog__title {
  color: rgb(var(--v-theme-on-surface)) !important;
  padding: 24px 24px 16px !important;
}

.confirm-dialog__text {
  color: rgba(255, 255, 255, 0.85) !important;
  padding: 0 24px 24px !important;
}

.confirm-dialog__actions {
  padding: 0 24px 24px !important;
  gap: 12px;
}

/* Botones con estilos del resto de la app */
.confirm-dialog__btn {
  border-radius: 14px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  min-height: 40px !important;
  padding: 10px 24px !important;
}

.confirm-dialog__btn--cancel {
  background: rgb(51, 51, 51) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgb(90, 90, 90) !important;
}

.confirm-dialog__btn--cancel:hover {
  background: rgb(60, 60, 60) !important;
  border-color: rgb(110, 110, 110) !important;
}

.confirm-dialog__btn--confirm {
  border-radius: 14px !important;
}
</style>
